import { Fragment } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import { CollectionCard } from "@/components/confluence/templates/collection-card";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";
import type { TemplateCollection } from "@/content/templates/template.types";
import { useLocalizedHref } from "@/libraries/web-framework";
import { CollectionOrderKey } from "@/templates/software/confluence/templates/confluence-templates-page/src/config/collection-order.ts";

interface CollectionRelatedSectionProps {
    collections?: TemplateCollection[];
}

const sectionStyle = css({
    paddingBlockStart: 96
});
const headingStyle = css({
    fontSize: "1.5rem",
    lineHeight: "2rem",
    fontWeight: 500,
    margin: 0,

    [media.below.sm]: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem"
    }
});
const collectionsBox = css({
    paddingBlockStart: 32,
    display: "flex",
    gap: 32,
    flexWrap: "wrap",

    [media.below.sm]: {
        gap: 16
    }
});
const hrefStyle = css({
    "color": "#0052cc",
    "fontWeight": 500,
    "fontSize": "1rem",
    "lineHeight": "1.25rem",
    "textDecoration": "underline",
    "marginInlineStart": 16,

    ":hover": {
        color: "#0065ff",
        textDecoration: "none"
    },
    ":active": {
        textDecoration: "none"
    },
    ":focus": {
        textDecoration: "none"
    },
    ":visited": {
        color: "#0052cc"
    }
});

const backWrapper = css({
    textAlign: "center",
    paddingBlockStart: 96,
    paddingBlockEnd: 96
});

export const CollectionRelatedSection = ({
    collections
}: CollectionRelatedSectionProps) => {
    const viewAllHref = useLocalizedHref("/software/confluence/templates");

    const firstTwoCollections = collections?.slice(0, 2);
    if (!firstTwoCollections || firstTwoCollections.length === 0) {
        return null;
    }
    return (
        <Fragment>
            <section css={sectionStyle}>
                {firstTwoCollections && firstTwoCollections.length > 0 && (
                    <Fragment>
                        <h3 css={headingStyle}>
                            <FormattedMessage
                                {...ConfluenceTemplateMessages.moreCollections}
                            />
                            <a
                                href={viewAllHref}
                                css={hrefStyle}
                                {...ConfluenceTemplateAnalytics.viewAllCollections()}
                            >
                                <FormattedMessage
                                    {...ConfluenceTemplateMessages.viewAllCollections}
                                />
                            </a>
                        </h3>
                        <div css={collectionsBox}>
                            {firstTwoCollections.map((collection) => (
                                <CollectionCard
                                    key={collection.collectionId}
                                    urlSlug={
                                        collection.urlSlug as CollectionOrderKey
                                    }
                                    name={collection.name ?? ""}
                                    templateCount={
                                        collection.howToUseThisCollection
                                            ?.collectionSteps?.length ?? 0
                                    }
                                />
                            ))}
                        </div>
                    </Fragment>
                )}
            </section>
            <div css={backWrapper}>
                <a
                    href={viewAllHref}
                    css={hrefStyle}
                    {...ConfluenceTemplateAnalytics.backToGalleryAnalyticProps()}
                >
                    <FormattedMessage
                        {...ConfluenceTemplateMessages.backToGallery}
                    />
                </a>
            </div>
        </Fragment>
    );
};
